import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import { getProfitMonitors, takeProfitMonitor } from '../../services/ProfitMonitorsService';
import { getSettings } from '../../services/SettingsService';
import ProfitGoalBar from '../ProfitMonitor/ProfitGoalBar';
import '../ProfitMonitor/ProfitMonitor.css';

const ACTIVE = new Set(['WATCHING', 'IN_POSITION']);

const STATUS_LABELS = {
    WATCHING: 'Acompanhando',
    IN_POSITION: 'Em posição'
};

function apiError(err) {
    const data = err?.response?.data;
    if (typeof data === 'string') return data;
    return data?.message || err?.message || 'Não foi possível ler os indicadores.';
}

function pairLabel(symbol) {
    return String(symbol || '').replace(/USDC$/, '/USDC').replace(/USDT$/, '/USDC');
}

function hasIndicators(indicators) {
    if (!indicators) return false;
    return [indicators.macd, indicators.rsi, indicators.ema20, indicators.ema50]
        .some(value => value != null);
}

function analysisFromMonitor(monitor) {
    if (!hasIndicators(monitor?.indicators))
        return null;
    return { indicators: monitor.indicators };
}

function isBuyReady(analysis) {
    if (analysis?.automationReady) return true;
    const ind = analysis?.indicators;
    if (!ind) return false;
    return ind.macd != null && ind.macdSignal != null && ind.macd > ind.macdSignal
        && ind.rsi != null && ind.rsi < 65
        && ind.ema20 != null && ind.ema50 != null && ind.ema20 > ind.ema50;
}

function PredadorStatus() {
    const navigate = useNavigate();
    const [monitors, setMonitors] = useState([]);
    const [analyses, setAnalyses] = useState({});
    const [analysisErrors, setAnalysisErrors] = useState({});
    const [loading, setLoading] = useState(true);
    const [sellingId, setSellingId] = useState(null);
    const [cooldownMin, setCooldownMin] = useState(30);
    const [notification, setNotification] = useState({ type: '', text: '' });

    useEffect(() => {
        let cancelled = false;

        async function load(first) {
            try {
                const data = await getProfitMonitors();
                if (cancelled) return;
                const active = (data.rows || []).filter(row => ACTIVE.has(row.status));
                setMonitors(active);
                active.forEach(monitor => {
                    const fromList = analysisFromMonitor(monitor);
                    if (fromList)
                        setAnalyses(prev => (prev[monitor.id] ? prev : { ...prev, [monitor.id]: fromList }));
                });
            } catch (err) {
                if (!cancelled)
                    setNotification({ type: 'error', text: apiError(err) });
            } finally {
                if (!cancelled && first) setLoading(false);
            }
        }

        load(true);
        getSettings()
            .then(result => {
                if (cancelled) return;
                const wait = parseInt(result?.predadorCooldownMin, 10);
                setCooldownMin(Number.isFinite(wait) ? Math.min(180, Math.max(0, wait)) : 30);
            })
            .catch(() => { /* mantém 30 */ });
        const timer = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            load(false);
        }, 40000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, []);

    async function takeProfit(event, monitor) {
        event.preventDefault();
        event.stopPropagation();
        if (sellingId) return;
        const pair = pairLabel(monitor.symbol);
        const perc = monitor.profitPerc != null
            ? ` (${monitor.profitPerc > 0 ? '+' : ''}${Number(monitor.profitPerc).toFixed(2)}%)`
            : '';
        const waitText = cooldownMin <= 0
            ? 'sem espera para recompra'
            : cooldownMin === 1
                ? '1 minuto sem recompra'
                : `${cooldownMin} minutos sem recompra`;
        if (!window.confirm(`Realizar o lucro de ${pair}${perc}?\nVende a mercado, exclui o monitor e este par fica ${waitText}.`))
            return;

        setSellingId(monitor.id);
        try {
            const result = await takeProfitMonitor(monitor.id);
            setMonitors(current => current.filter(row => row.id !== monitor.id));
            setNotification({
                type: 'success',
                text: result?.message || `Lucro de ${pair} realizado. Este par fica ${waitText}.`
            });
        } catch (err) {
            setNotification({ type: 'error', text: apiError(err) });
        } finally {
            setSellingId(null);
        }
    }

    const hunting = monitors.filter(row => row.status === 'WATCHING').length;
    const inPosition = monitors.filter(row => row.status === 'IN_POSITION').length;
    const ready = monitors.filter(row =>
        row.status === 'WATCHING' && isBuyReady(analyses[row.id])
    ).length;
    const predadorCount = monitors.filter(row => row.isPredador).length;

    return (
        <>
            <main className="content predador-status-layout">
                <div className="profit-monitor predador-status">
                    <header className="profit-monitor-header">
                        <div className="predador-status-top">
                            <button
                                type="button"
                                className="predador-status-back"
                                onClick={() => navigate('/profit-monitor')}
                            >
                                ← Monitor de Lucros
                            </button>
                        </div>
                        <h1>Trades em andamento</h1>
                        <p>
                            O trade acompanha a alta da moeda. O piso é 1,2%. O topo é livre: pode ser 3%, 5% ou 7%. A venda sai na reversão.
                            Verde = regra ok. Vermelho = o que está impedindo a compra ou pedindo a venda.
                        </p>
                    </header>

                    <div className="predador-status-summary">
                        <div className="profit-card predador-stat">
                            <span>Ativos</span>
                            <strong>{monitors.length}</strong>
                        </div>
                        <div className="profit-card predador-stat">
                            <span>Automáticos</span>
                            <strong>{predadorCount}</strong>
                        </div>
                        <div className="profit-card predador-stat">
                            <span>Acompanhando</span>
                            <strong>{hunting}</strong>
                        </div>
                        <div className="profit-card predador-stat">
                            <span>Em posição</span>
                            <strong>{inPosition}</strong>
                        </div>
                        <div className="profit-card predador-stat ready">
                            <span>Prontos para comprar</span>
                            <strong>{ready}</strong>
                        </div>
                    </div>

                    {loading && <div className="profit-loading">Carregando trades...</div>}

                    {!loading && !monitors.length && (
                        <div className="profit-card">
                            <div className="profit-empty">
                                Nenhum trade ativo agora. Ligue uma opção de trade nas configurações ou inicie um Monitor de Lucros.
                            </div>
                        </div>
                    )}

                    <div className="predador-status-grid">
                        {monitors.map(monitor => (
                            <section
                                key={monitor.id}
                                className={`profit-card predador-status-card ${monitor.status === 'IN_POSITION' ? 'in-position' : ''}`}
                                onClick={() => navigate(`/profit-monitor/${monitor.symbol}?interval=${monitor.interval || '15m'}`)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        navigate(`/profit-monitor/${monitor.symbol}?interval=${monitor.interval || '15m'}`);
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                            >
                                <div className="predador-status-card-head">
                                    <div>
                                        <strong>{pairLabel(monitor.symbol)}</strong>
                                        {monitor.isPredador
                                            ? <span className="badge bg-warning text-dark ms-2">{monitor.buyOnly ? 'Só compra' : 'Trade automático'}</span>
                                            : <span className="badge bg-secondary ms-2">Manual</span>}
                                        <div className="predador-status-meta">
                                            {monitor.interval}
                                            {' · '}
                                            {STATUS_LABELS[monitor.status] || monitor.status}
                                            {monitor.status === 'IN_POSITION' && monitor.profitPerc != null
                                                ? ` · ${monitor.profitPerc > 0 ? '+' : ''}${Number(monitor.profitPerc).toFixed(2)}%`
                                                : ''}
                                        </div>
                                    </div>
                                </div>
                                {analyses[monitor.id]
                                    ? <ProfitGoalBar compact analysis={analyses[monitor.id]} monitor={monitor} />
                                    : <p className="profit-empty">
                                        {analysisErrors[monitor.id] || 'Lendo indicadores...'}
                                    </p>}
                                {monitor.status === 'IN_POSITION' && (
                                    <button
                                        type="button"
                                        className="predador-take-profit"
                                        disabled={sellingId === monitor.id}
                                        onClick={event => takeProfit(event, monitor)}
                                    >
                                        {sellingId === monitor.id ? 'Vendendo...' : 'Realizar lucros'}
                                    </button>
                                )}
                            </section>
                        ))}
                    </div>
                </div>
            </main>
            <Footer />
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

export default PredadorStatus;
